import React, { createContext, useContext, useReducer, type Dispatch } from "react";

export interface Widget {
  id: string;
  type: string;
  title: string;
  data: Record<string, unknown>;
  position: { x: number; y: number };
  size: { w: number; h: number };
  minimized: boolean;
  zIndex: number;
}

export type WidgetDispatchAction =
  | { type: "CREATE"; widget: Widget }
  | { type: "UPDATE"; widgetId: string; data: Record<string, unknown> }
  | { type: "CLOSE"; widgetId: string }
  | { type: "MOVE"; widgetId: string; position: { x: number; y: number } }
  | { type: "RESIZE"; widgetId: string; size: { w: number; h: number } }
  | { type: "MINIMIZE"; widgetId: string }
  | { type: "RESTORE"; widgetId: string }
  | { type: "FOCUS"; widgetId: string };

interface WidgetContextValue {
  widgets: Widget[];
  dispatch: Dispatch<WidgetDispatchAction>;
}

const WidgetContext = createContext<WidgetContextValue>({
  widgets: [],
  dispatch: () => {},
});

let nextZIndex = 100;

function widgetReducer(state: Widget[], action: WidgetDispatchAction): Widget[] {
  switch (action.type) {
    case "CREATE":
      return [...state, { ...action.widget, zIndex: ++nextZIndex }];
    case "UPDATE":
      return state.map((w) =>
        w.id === action.widgetId ? { ...w, data: { ...w.data, ...action.data } } : w,
      );
    case "CLOSE":
      return state.filter((w) => w.id !== action.widgetId);
    case "MOVE":
      return state.map((w) =>
        w.id === action.widgetId ? { ...w, position: action.position } : w,
      );
    case "RESIZE":
      return state.map((w) =>
        w.id === action.widgetId ? { ...w, size: action.size } : w,
      );
    case "MINIMIZE":
      return state.map((w) =>
        w.id === action.widgetId ? { ...w, minimized: true } : w,
      );
    case "RESTORE":
      return state.map((w) =>
        w.id === action.widgetId ? { ...w, minimized: false, zIndex: ++nextZIndex } : w,
      );
    case "FOCUS":
      return state.map((w) =>
        w.id === action.widgetId ? { ...w, zIndex: ++nextZIndex } : w,
      );
    default:
      return state;
  }
}

export function WidgetProvider({ children }: { children: React.ReactNode }) {
  const [widgets, dispatch] = useReducer(widgetReducer, []);

  return (
    <WidgetContext.Provider value={{ widgets, dispatch }}>
      {children}
    </WidgetContext.Provider>
  );
}

export function useWidgets() {
  return useContext(WidgetContext);
}
